import { Controller } from "@hotwired/stimulus";
import { animate } from "animejs";

const ANIM_DURATION = 220;

export default class PanelCollapseController extends Controller {
  static targets = ["header", "body", "toggle", "chevron"];
  static values = { panelId: String };

  connect() {
    this._saveTimeout = null;
  }

  disconnect() {
    clearTimeout(this._saveTimeout);
  }

  toggle() {
    const panel = this.element;
    const wasCollapsed = panel.classList.contains("sc-collapsed");

    if (wasCollapsed) {
      this._expand(panel);
    } else {
      this._collapse(panel);
    }

    if (this.hasChevronTarget) {
      this.chevronTarget.className = `fa-solid ${wasCollapsed ? "fa-chevron-up" : "fa-chevron-down"} text-xs`;
    }
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-expanded", wasCollapsed ? "true" : "false");
    }

    this._scheduleSave(!wasCollapsed);
  }

  _expand(panel) {
    const origBottom = panel.offsetTop + panel.offsetHeight;
    panel.classList.remove("sc-collapsed");

    if (!this.hasBodyTarget) {
      const delta = panel.offsetHeight - origBottom + panel.offsetTop;
      this._dispatchToggle(panel, false, origBottom, delta);
      return;
    }

    // Body is at h:0, opacity:0 per CSS (sc-collapsed removed).
    // Use scrollHeight to measure natural content height, then animate in.
    const naturalH = this.bodyTarget.scrollHeight;
    this._dispatchToggle(panel, false, origBottom, naturalH);

    animate(this.bodyTarget, {
      height: [0, naturalH],
      opacity: [0, 1],
      duration: ANIM_DURATION,
      ease: "outCubic",
      onComplete: () => {
        this.bodyTarget.style.height = "";
        this.bodyTarget.style.opacity = "";
      },
    });
  }

  _collapse(panel) {
    const oldHeight = panel.offsetHeight;
    const origBottom = panel.offsetTop + oldHeight;

    if (!this.hasBodyTarget) {
      panel.classList.add("sc-collapsed");
      this._dispatchToggle(panel, true, origBottom, panel.offsetHeight - oldHeight);
      return;
    }

    const naturalH = this.bodyTarget.offsetHeight;
    this._dispatchToggle(panel, true, origBottom, -naturalH);

    this.bodyTarget.style.height = naturalH + "px";
    this.bodyTarget.style.overflow = "hidden";

    animate(this.bodyTarget, {
      height: 0,
      opacity: 0,
      duration: ANIM_DURATION,
      ease: "outCubic",
      onComplete: () => {
        panel.classList.add("sc-collapsed");
        this.bodyTarget.style.height = "";
        this.bodyTarget.style.overflow = "";
        this.bodyTarget.style.opacity = "";
      },
    });
  }

  _dispatchToggle(panel, collapsed, originalBottom, delta) {
    panel.dispatchEvent(
      new CustomEvent("panel:toggle", {
        bubbles: true,
        detail: { collapsed, originalBottom, delta },
      }),
    );
  }

  _scheduleSave(collapsed) {
    clearTimeout(this._saveTimeout);
    this._saveTimeout = setTimeout(() => {
      const panelId = this.panelIdValue;
      if (!panelId) return;
      fetch("/api/v1/user_preferences/panel_collapse", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ panel_id: panelId, collapsed }),
      }).catch((err) => console.error("[panel-collapse] persist failed", err));
    }, 300);
  }
}
